import { GridColDef } from "@mui/x-data-grid-pro";
import {
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  GKButton,
  GKDataGrid,
  GKTextField,
  Grid2,
  SearchIcon,
  Typography,
} from "components";
import { useFetchQuery } from "hooks/useQueries";
import { useState } from "react";
import { extraColumnsField } from "utils/helpers";
import PerformanceChat from "./PerformanceChat";

interface Props {
  allHoldingsInsightsData: any;
  portfolios: any;
  portfolioList: any;
}

const Performance = (prop: Props) => {
  const { allHoldingsInsightsData, portfolios, portfolioList } = prop;

  const selectedPortfolios = portfolioList.filter((p: any) =>
    portfolios.includes(p.id),
  );

  const { data, isLoading } = useFetchQuery({
    key: ["GET_MF_RETURNS", portfolios],
    route: `/holdings/mf_returns/?portfolios=[${portfolios.join("%2C")}]`,
  });

  const [selectedBtn, setSelectedBtn] = useState("All");
  const [search, setSearch] = useState<Record<string, string>>({});
  const [selectedAssets, setSelectedAssets] = useState<
    { gcode: string; name: string }[]
  >([]);

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Scheme Name", "name", 130, 1),
    },
    {
      ...extraColumnsField("1 Year", "1Y", 50, 1),
      align: "right",
      headerAlign: "right",
    },
    {
      ...extraColumnsField("3 Year", "3Y", 50, 1),
      align: "right",
      headerAlign: "right",
    },
    {
      ...extraColumnsField("5 Year", "5Y", 50, 1),
      align: "right",
      headerAlign: "right",
    },
  ];

  // Grouping Data
  const grouped = {
    MF: allHoldingsInsightsData?.filter((x: any) => x.instType === "MF") || [],
    EQ: allHoldingsInsightsData?.filter((x: any) => x.instType === "EQ") || [],
    ETF:
      allHoldingsInsightsData?.filter((x: any) => x.instType === "ETF") || [],
  };

  const MFCount = grouped.MF.length;
  const EQCount = grouped.EQ.length;
  const ETFCount = grouped.ETF.length;

  // Show Message Logic
  const showNoData =
    (selectedBtn === "All" && MFCount === 0 && EQCount === 0) ||
    (selectedBtn === "MF" && MFCount === 0) ||
    (selectedBtn === "EQ" && EQCount === 0) ||
    (selectedBtn === "ETF" && ETFCount === 0);

  const categories = [
    {
      label: "Mutual Funds",
      type: "MF",
      list: grouped.MF,
    },
    {
      label: "Equity",
      type: "EQ",
      list: grouped.EQ,
    },
    {
      label: "ETFs",
      type: "ETF",
      list: grouped.ETF,
    },
  ];

  const visibleCategories =
    selectedBtn === "All"
      ? categories
      : categories.filter((c) => c.type === selectedBtn);

  const NoDataView = () => (
    <Grid2
      size={12}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      minHeight="60vh"
      textAlign="center"
      px={2}
    >
      <Typography fontSize={12} fontWeight={700} color="var(--grey-900)">
        No Investments Available for Comparison
      </Typography>

      <Typography
        fontSize={12}
        fontWeight={400}
        color="var(--grey-500)"
        mt={0.5}
      >
        Add holdings to the client&apos;s portfolio to compare performance
        against the benchmark.
      </Typography>
    </Grid2>
  );

  return (
    <>
      <Card sx={{ p: 0 }}>
        <Grid2 container>
          <Grid2
            size={9.5}
            sx={{
              p: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #E3E8EF",
            }}
          >
            <Typography fontSize={14} fontWeight={700} color="var(--grey-900)">
              NAV comparison with Benchmark
            </Typography>
          </Grid2>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: "#E3E8EF" }}
          />

          <Grid2
            size={2.4}
            sx={{
              p: 1,
              borderBottom: "1px solid #E3E8EF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <GKButton
              variant={selectedBtn === "All" ? "contained" : "lightBg"}
              onClick={() => setSelectedBtn("All")}
            >
              All
            </GKButton>

            <GKButton
              variant={selectedBtn === "EQ" ? "contained" : "lightBg"}
              onClick={() => setSelectedBtn("EQ")}
            >
              Equity
            </GKButton>

            <GKButton
              variant={selectedBtn === "MF" ? "contained" : "lightBg"}
              onClick={() => setSelectedBtn("MF")}
            >
              MF
            </GKButton>
          </Grid2>
        </Grid2>

        <Grid2 container>
          <Grid2 size={9.5} py={2} pr={1}>
            <PerformanceChat
              portfolios={portfolios}
              selectedPortfolios={selectedPortfolios}
              assets={selectedAssets}
            />
          </Grid2>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: "#E3E8EF" }}
          />

          <Grid2
            size={2.4}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "570px",
            }}
          >
            <Grid2
              sx={{
                p: 2,
                pb: 1,
                backgroundColor: "#fff",
                borderBottom: "1px solid #E3E8EF",
              }}
            >
              <GKTextField
                size="small"
                fullWidth
                onChange={(e) => setSearch({ value: e.target.value })}
                value={search.value || ""}
                slotProps={{
                  input: {
                    startAdornment: (
                      <SearchIcon width={20} color={"var(--grey-500)"} />
                    ),
                    placeholder: "Search Equity, MFs or ETFs",
                  },
                }}
              />
            </Grid2>

            <Grid2
              sx={{
                flex: 1,
                overflowY: "auto",
                pt: 1,
              }}
            >
              {showNoData && <NoDataView />}

              {!showNoData &&
                visibleCategories.map((cat) => {
                  const searchValue = search.value?.toLowerCase() || "";

                  const filteredList = cat.list.filter((item: any) =>
                    item.asset?.name?.toLowerCase().includes(searchValue),
                  );
                  if (cat.list.length === 0) return null;

                  return (
                    <Grid2 key={cat.type}>
                      <Grid2 display="flex" alignItems="center" px={2} py={1}>
                        <Typography
                          fontSize={12}
                          fontWeight={600}
                          color="var(--black-900)"
                          mr={0.5}
                        >
                          {cat.label}
                        </Typography>

                        <Typography
                          fontSize={12}
                          fontWeight={400}
                          color="var(--grey-700)"
                        >
                          ({filteredList.length})
                        </Typography>
                      </Grid2>
                      {filteredList.length > 0 && (
                        <Grid2
                          display="flex"
                          flexDirection="column"
                          gap={0.4}
                          px={2}
                        >
                          {filteredList.map((item: any) => (
                            <FormControlLabel
                              key={item.asset?.gcode}
                              control={
                                <Checkbox
                                  sx={{ p: 0.2, mr: 0.5 }}
                                  checked={selectedAssets.some(
                                    (asset) =>
                                      asset.gcode === item.asset?.gcode,
                                  )}
                                  disabled={
                                    !selectedAssets.some(
                                      (asset) =>
                                        asset.gcode === item.asset?.gcode,
                                    ) && selectedAssets.length >= 5
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (selectedAssets.length < 5) {
                                        setSelectedAssets((prev) => [
                                          ...prev,
                                          {
                                            gcode: item.asset?.gcode,
                                            name: item.asset?.name,
                                          },
                                        ]);
                                      }
                                    } else {
                                      setSelectedAssets((prev) =>
                                        prev.filter(
                                          (asset) =>
                                            asset.gcode !== item.asset?.gcode,
                                        ),
                                      );
                                    }
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  fontSize={12}
                                  fontWeight={400}
                                  color="#333"
                                >
                                  {item.asset?.name}
                                </Typography>
                              }
                            />
                          ))}
                        </Grid2>
                      )}{" "}
                    </Grid2>
                  );
                })}
            </Grid2>
          </Grid2>
        </Grid2>
      </Card>

      <Card sx={{ mt: 2, p: 0 }}>
        <Grid2 size={12} p={1.5}>
          <Typography fontSize={14} fontWeight={700} color="var(--grey-900)">
            Performance of schemes by Years
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <GKDataGrid
            columns={columns}
            rows={
              data?.data?.data?.map((dataItem: any, index: number) => ({
                ...dataItem,
                id: index,
              })) || []
            }
            loading={isLoading}
            name="mfPerformance"
            density="compact"
            showSearch={false}
            showHeaderGrid={false}
          />
        </Grid2>
      </Card>
    </>
  );
};

export default Performance;
